import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ArrowRight, Calendar, Activity, Plane, ListFilter, Trash2, AlertTriangle, FolderOpen } from 'lucide-react';
import { projectsService, Project } from '../services/projects';
import { useLanguage } from '../context/LanguageContext';

/*  ──────────────────────────────────────────────────────────
    Aipron DACAMS — Analysis HUB / Home Page (v3 Corporate)
    ────────────────────────────────────────────────────────── */

const STATUS_CONFIG = {
  COMPLETED: {
    label: { es: 'COMPLETE', en: 'COMPLETE' },
    barColor: '#1D7D50',
    badgeClass: 'badge-success'
  },
  PROCESSING: {
    label: { es: 'PROCESSING', en: 'PROCESSING' },
    barColor: '#B07D16',
    badgeClass: 'badge-warning'
  },
  FAILED: {
    label: { es: 'ERROR', en: 'ERROR' },
    barColor: '#C33333',
    badgeClass: 'badge-danger'
  },
  PENDING: {
    label: { es: 'PENDING', en: 'PENDING' },
    barColor: '#999',
    badgeClass: 'badge-neutral'
  },
} as const;

export const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const lang = language as 'es' | 'en';

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      const data = await projectsService.getAll();
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await projectsService.delete(deleteId);
      setDeleteId(null);
      loadProjects();
    } catch {
      alert('Error eliminando.');
    }
  };

  const processedProjects = projects
    .filter(p => {
      const search = searchTerm.toLowerCase();
      return (
        p.name.toLowerCase().includes(search) ||
        p.iata_code?.toLowerCase().includes(search) ||
        p.icao_code?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      return a.name.localeCompare(b.name);
    });

  const getStatus = (project: Project) => {
    const key = (project.analysis_status as keyof typeof STATUS_CONFIG) || 'PENDING';
    return STATUS_CONFIG[key] || STATUS_CONFIG.PENDING;
  };

  return (
    <div className="page-container relative min-h-full">

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-modal flex items-center justify-center p-4 animate-fade-in"
             style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="pro-card p-8 w-full max-w-md text-center animate-scale-in">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
                 style={{ background: 'rgba(195,51,51,0.08)' }}>
              <AlertTriangle className="w-7 h-7" style={{ color: 'var(--aipron-danger)' }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
              {t('messages', 'confirmDelete')}
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              {lang === 'es' ? 'Esta acción no se puede deshacer.' : 'This action cannot be undone.'}
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary">
                {t('common', 'cancel')}
              </button>
              <button onClick={handleDeleteConfirm} className="btn-danger">
                {lang === 'es' ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-8xl mx-auto">

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pb-6"
             style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div>
            <span className="eyebrow">DACAMS Analysis</span>
            <h1 className="page-title text-3xl mt-1">Analysis HUB</h1>
          </div>
          <button
            onClick={() => navigate('/projects/new')}
            className="btn-primary"
          >
            <Plus size={18} />
            {lang === 'es' ? 'Nuevo Análisis' : 'New Analysis'}
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-3 mb-8 max-w-4xl">
          {/* Search */}
          <div className="flex-1 flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150"
               style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)' }}>
            <Search className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              placeholder={lang === 'es' ? 'Buscar por nombre, IATA o ICAO...' : 'Search by name, IATA or ICAO...'}
              className="w-full bg-transparent focus:outline-none text-sm"
              style={{ color: 'var(--text-primary)' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg"
               style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)' }}>
            <ListFilter className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
              className="bg-transparent outline-none text-sm font-medium cursor-pointer"
              style={{ color: 'var(--text-secondary)' }}
            >
              <option value="date">{lang === 'es' ? 'Fecha' : 'Date'}</option>
              <option value="name">{lang === 'es' ? 'Nombre' : 'Name'}</option>
            </select>
          </div>
        </div>

        {/* Project Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="pro-card overflow-hidden">
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="skeleton w-12 h-12 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="skeleton-title" />
                      <div className="skeleton h-3 w-20" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : processedProjects.length === 0 ? (
          <div className="empty-state py-20 rounded-xl"
               style={{ border: '2px dashed var(--border)', background: 'var(--surface-raised)' }}>
            <FolderOpen className="empty-state-icon" />
            <h3 className="empty-state-title">
              {searchTerm
                ? (lang === 'es' ? 'Sin resultados' : 'No results found')
                : (lang === 'es' ? 'No hay proyectos activos' : 'No active projects')
              }
            </h3>
            <p className="empty-state-desc mb-6">
              {searchTerm
                ? (lang === 'es' ? `No se encontraron proyectos para "${searchTerm}"` : `No projects found for "${searchTerm}"`)
                : (lang === 'es' ? 'Crea tu primer análisis para empezar.' : 'Create your first analysis to start.')
              }
            </p>
            {!searchTerm && (
              <button onClick={() => navigate('/projects/new')} className="btn-primary">
                <Plus size={16} />
                {lang === 'es' ? 'Crear Análisis' : 'Create Analysis'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {processedProjects.map((project, idx) => {
              const status = getStatus(project);
              return (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="pro-card group cursor-pointer overflow-hidden relative flex animate-fade-in"
                  style={{ animationDelay: `${Math.min(idx * 50, 300)}ms`, animationFillMode: 'both' }}
                >
                  {/* Left status bar */}
                  <div className="w-[3px] flex-shrink-0 rounded-l-[10px]"
                       style={{ background: status.barColor }} />

                  {/* Delete button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteId(project.id); }}
                    className="absolute top-3 right-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-150"
                    style={{ color: 'var(--text-tertiary)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(195,51,51,0.08)'; e.currentTarget.style.color = 'var(--aipron-danger)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    aria-label="Delete project"
                  >
                    <Trash2 size={14} />
                  </button>

                  {/* Content */}
                  <div className="flex-1 p-5">
                    <div className="flex items-center gap-4 mb-3">
                      {/* IATA badge */}
                      <div className="h-11 w-11 rounded-lg flex items-center justify-center text-[13px] font-bold"
                           style={{
                             fontFamily: 'var(--font-mono)',
                             background: 'var(--surface-raised)',
                             border: '1px solid var(--border-subtle)',
                             color: 'var(--text-primary)',
                           }}>
                        {project.iata_code || <Plane size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[15px] truncate transition-colors duration-150"
                            style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                          {project.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                                style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-raised)', color: 'var(--text-tertiary)', border: '1px solid var(--border-subtle)' }}>
                            {project.icao_code || '----'}
                          </span>
                          <span className={status.badgeClass}>
                            {status.label[lang]}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Footer row */}
                    <div className="flex justify-between items-center pt-3"
                         style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                          <Calendar size={11} />
                          {project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                          <Activity size={11} />
                          {project.has_file ? (lang === 'es' ? 'Datos OK' : 'Data OK') : (lang === 'es' ? 'Pendiente' : 'Pending')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                           style={{ color: 'var(--aipron-accent)' }}>
                        <span>{lang === 'es' ? 'Ver' : 'View'}</span>
                        <ArrowRight size={12} className="transition-transform duration-150 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};